import React, { useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Search as SearchIcon, Crown, Compass } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalPlayerControls } from '@/hooks/useGlobalPlayerControls';
import { JOURNEY_STAGES, CATEGORIES } from '@/constants/playlists';
import { Song } from '@/types/db';
import IPadWrapper from './IPadWrapper';
import { useSongSearch } from '@/hooks/useSongSearch';

const TopBarComponent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { playCategorySong, playJourneySong } = useGlobalPlayerControls();
  
  // Usa o hook centralizado de busca
  const {
    searchTerm,
    setSearchTerm,
    results,
    loading,
    showDropdown,
    setShowDropdown,
    forceSearch,
    clearSearch
  } = useSongSearch({ debounceMs: 300 });

  // 🚀 OTIMIZAÇÃO: Memoiza journeySongIds para evitar recálculo
  const journeySongIds = useMemo(() => JOURNEY_STAGES.flatMap(stage => stage.songIds), []);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        clearSearch();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [clearSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    // Mostra dropdown imediatamente quando começa a digitar
    if (value.trim().length > 0) {
      setShowDropdown(true);
    }
  };

  const handleResultClick = async (song: any) => {
    try {
      if (song.is_free === false && user?.tipo_de_conta !== 'premium') {
        // TODO: Implementar notificação de erro sem toast
        return;
      }

      // Limpa o estado da busca
      clearSearch();
      
      // Garante que temos o ID correto da música
      const songId = Number(song.id);
      if (!songId) {
        return;
      }

      // Prepara a lista de IDs para reprodução, mantendo a ordem dos resultados
      const songIds = results
        .map((r: any) => Number(r.id))
        .filter(id => !isNaN(id));

      // Verifica se a música está na jornada
      if (journeySongIds.includes(songId)) {
        await playJourneySong(songId);
      } else {
        // Se a música pertence a uma categoria, monta a fila da categoria com a música clicada primeiro
        let foundCategory = null;
        for (const [catId, cat] of Object.entries(CATEGORIES)) {
          if (cat.songIds.includes(songId)) {
            foundCategory = catId;
            break;
          }
        }
        
        if (foundCategory) {
          let categoryIds = [...CATEGORIES[foundCategory].songIds];
          // Remove o songId se já estiver na lista e coloca no início
          categoryIds = categoryIds.filter(id => id !== songId);
          categoryIds.unshift(songId);
          await playCategorySong(songId, foundCategory, categoryIds);
        } else {
          // Fallback: usa a primeira categoria disponível
          const firstCategory = Object.keys(CATEGORIES)[0];
          if (!songIds.includes(songId)) {
            songIds.unshift(songId);
          }
          await playCategorySong(songId, firstCategory, songIds);
        }
      }

      if (location.pathname !== '/player') {
        navigate('/player');
      }
      inputRef.current?.blur();
    } catch (error) {
      console.error('Erro ao carregar música:', error);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      clearSearch();
      return;
    }

    // Força busca imediata quando Enter é pressionado (sem debounce)
    await forceSearch(searchTerm);
  };

  const handleRandomPlay = async () => {
    if (!results || results.length === 0) return;
    
    try {
      // Para usuários premium: qualquer música
      // Para usuários gratuitos: apenas músicas grátis
      const availableSongs = user?.tipo_de_conta === 'premium'
        ? results
        : results.filter(song => song.is_free !== false);
      
      if (availableSongs.length === 0) return;
      
      const randomSong = availableSongs[Math.floor(Math.random() * availableSongs.length)];
      const songId = randomSong.id;
      const songIds = availableSongs.map(s => s.id);
      
      // Encontrar categoria da música para usar playCategorySong
      let category = null;
      for (const [categoryKey, categoryData] of Object.entries(CATEGORIES)) {
        if (categoryData.songIds.includes(songId)) {
          category = categoryKey;
          break;
        }
      }
      
      if (category) {
        await playCategorySong(songId, category, songIds);
      } else {
        // Fallback: usa a primeira categoria encontrada
        const firstCategory = Object.keys(CATEGORIES)[0];
        await playCategorySong(songId, firstCategory, songIds);
      }
      
    } catch (error) {
      console.error('Erro ao tocar música aleatória:', error);
    }
  };

  return (
    <IPadWrapper>
      <div 
        className="topbar-safe fixed top-0 left-0 right-0 w-full bg-background z-50 border-b border-[#FFD700] rounded-b-2xl topbar"
      >
        <div className="container mx-auto px-4 md:px-6 lg:px-8 w-full flex items-center justify-between h-14 md:h-16 lg:h-20 py-1 md:py-1.5 lg:py-2">
          <form onSubmit={handleSearch} className="w-full max-w-full relative flex items-center">
            <SearchIcon className="absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 text-gray-600 h-5 w-5 md:h-6 md:w-6 lg:h-7 lg:w-7 pointer-events-none" />
            <Input
              ref={inputRef}
              type="text"
              placeholder="Buscar músicas..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="h-10 md:h-12 lg:h-14 text-base md:text-lg lg:text-xl pl-12 md:pl-14 lg:pl-16 pr-4 md:pr-6 bg-white border border-gray-300 text-gray-800 placeholder:text-gray-500 focus:ring-2 focus:ring-primary/30 focus:border-primary rounded-lg md:rounded-xl search-input"
              autoComplete="off"
            />
            {showDropdown && (
              <div
                ref={dropdownRef}
                className="absolute left-0 right-0 mt-1 md:mt-2 bg-[#B3CEE5]/90 backdrop-blur-sm rounded-xl shadow-lg z-50 max-h-96 overflow-y-auto border border-[#B3CEE5] scrollbar-thick scrollbar-thumb-[#23405a] scrollbar-track-[#B3CEE5] search-dropdown"
                style={{ WebkitOverflowScrolling: 'touch', top: '100%' }}
              >
                {loading ? (
                  <div className="p-4 md:p-6 text-center text-sm md:text-base text-[#23405a]">Buscando...</div>
                ) : results.length === 0 ? (
                  <div className="p-4 md:p-6 text-center text-sm md:text-base text-[#23405a]">Nenhum resultado</div>
                ) : (
                  <>
                    {results.map((song: any, idx: number) => {
                      const isPremium = song.is_free === false;
                      const isBlocked = isPremium && (!user || user.tipo_de_conta !== 'premium');
                      const isJourney = journeySongIds.includes(Number(song.id));
                      return (
                      <React.Fragment key={song.id}>
                        <button
                          type="button"
                            onClick={isBlocked ? undefined : () => handleResultClick(song)}
                            className={`w-full text-left px-4 md:px-6 lg:px-8 py-3 md:py-4 lg:py-5 flex items-center gap-3 md:gap-4 lg:gap-5 rounded-lg text-[#23405a] text-sm md:text-base lg:text-lg font-bold transition-colors
                              ${isBlocked ? 'cursor-not-allowed opacity-50 pointer-events-none select-none' : 'hover:bg-[#87CEEB]/60 focus:bg-[#87CEEB]/60'}`}
                            style={isBlocked ? { cursor: 'not-allowed' } : {}}
                            tabIndex={isBlocked ? -1 : 0}
                            aria-disabled={isBlocked}
                        >
                          <div className="relative w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 flex-shrink-0">
                            <img 
                              key={song.id}
                              src={song.capa_url}
                              alt={song.descricao}
                              className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded border border-[#B3CEE5]"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                            <div className="hidden w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded bg-gradient-to-br from-blue-50 to-blue-100 items-center justify-center">
                              <span className="text-sm md:text-base lg:text-lg text-blue-400">♪</span>
                            </div>
                          </div>
                          <span className="truncate flex-1 text-sm md:text-base lg:text-lg">{song.descricao}</span>
                          {/* Badge Jornada */}
                            {isJourney && (
                              <span className="ml-2 flex items-center justify-center bg-blue-600 text-white rounded shadow border border-blue-800 p-1 md:p-1.5 flex-shrink-0">
                                <Compass className="w-3 h-3 md:w-4 md:h-4 lg:w-5 lg:h-5" />
                            </span>
                          )}
                          {user?.tipo_de_conta !== 'premium' && song.is_free === true && (
                            <span className="ml-2 bg-green-500 text-white text-xs md:text-sm font-bold px-2 md:px-3 py-1 md:py-1.5 rounded shadow flex-shrink-0">Grátis</span>
                          )}
                          {user?.tipo_de_conta !== 'premium' && isPremium && (
                            <span className="ml-2 flex items-center flex-shrink-0">
                              <Crown className="w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 text-amber-400" fill="currentColor" />
                            </span>
                          )}
                        </button>
                        {idx < results.length - 1 && (
                          <div className="border-t-2 border-[#5A7D9A] mx-4 md:mx-6 lg:mx-8" />
                        )}
                      </React.Fragment>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </form>
        </div>
      </div>
    </IPadWrapper>
  );
};

// 🚀 OTIMIZAÇÃO: TopBar não recebe props, então memoização do componente não é necessária
// As otimizações internas (useMemo para journeySongIds) já são suficientes
export default TopBarComponent; 